"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { usePathname } from "next/navigation";
import { useMotionMode } from "@/context/MotionContext";
import { useThemeMode } from "@/context/ThemeContext";
import { useLoaderState } from "@/context/LoaderContext";
import { useBootSequence } from "@/hooks/useBootSequence";
import * as spatialAudio from "@/lib/audio/spatialAudio";

// Konami code: up up down down left right left right space
const KONAMI_CODE = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  " ",
];

function getBreakpoint(width: number): string {
  if (width < 640) return "xs";
  if (width < 768) return "sm";
  if (width < 1024) return "md";
  if (width < 1280) return "lg";
  if (width < 1536) return "xl";
  return "2xl";
}

export function DebugOverlay() {
  const [isVisible, setIsVisible] = useState(false);
  const [inputSequence, setInputSequence] = useState<string[]>([]);
  const [showGrid, setShowGrid] = useState(false);
  const [fps, setFps] = useState(0);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [gsapCount, setGsapCount] = useState(0);
  const [forcedGpuTier, setForcedGpuTier] = useState<number | null>(null);
  const [audioMuted, setAudioMuted] = useState(true);
  const [crtEnabled, setCrtEnabled] = useState(true);

  const { mode: motionMode, gpuTier, reducedMotion, canAnimate, backgrounded, setMode, setGpuTier } = useMotionMode();
  const { mode: themeMode } = useThemeMode();
  const { isLoaderActive } = useLoaderState();
  const pathname = usePathname();
  const boot = useBootSequence({ passive: true });

  const frameTimesRef = useRef<number[]>([]);
  const lastFrameTimeRef = useRef(performance.now());
  const rafRef = useRef<number | null>(null);

  // FPS counter
  useEffect(() => {
    if (!isVisible) return;

    const measureFps = () => {
      const now = performance.now();
      const delta = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;

      frameTimesRef.current.push(delta);
      if (frameTimesRef.current.length > 60) {
        frameTimesRef.current.shift();
      }

      const avgDelta = frameTimesRef.current.reduce((a, b) => a + b, 0) / frameTimesRef.current.length;
      setFps(Math.round(1000 / avgDelta));

      rafRef.current = requestAnimationFrame(measureFps);
    };

    rafRef.current = requestAnimationFrame(measureFps);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible]);

  // Viewport size
  useEffect(() => {
    const updateViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  // Scroll tracking
  useEffect(() => {
    if (!isVisible) return;

    const updateScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollY(Math.round(scrollTop));
      setScrollPercent(docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0);
    };

    updateScroll();
    window.addEventListener("scroll", updateScroll, { passive: true });
    return () => window.removeEventListener("scroll", updateScroll);
  }, [isVisible]);

  // GSAP animation count
  useEffect(() => {
    if (!isVisible) return;

    const checkGsap = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const gsap = (window as any).gsap;
      if (gsap) {
        const tweens = gsap.globalTimeline?.getChildren?.() || [];
        setGsapCount(tweens.filter((t: { isActive?: () => boolean }) => t.isActive?.()).length);
      }
    };

    const interval = setInterval(checkGsap, 500);
    return () => clearInterval(interval);
  }, [isVisible]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // ESC to close
      if (e.key === "Escape" && isVisible) {
        setIsVisible(false);
        return;
      }

      // Add key to sequence
      const key = e.key;
      const newSequence = [...inputSequence, key].slice(-KONAMI_CODE.length);
      setInputSequence(newSequence);

      // Check if sequence matches Konami code
      if (newSequence.length === KONAMI_CODE.length) {
        const matches = newSequence.every((k, i) => k === KONAMI_CODE[i]);
        if (matches) {
          setIsVisible((prev) => !prev);
          setInputSequence([]);
        }
      }
    },
    [inputSequence, isVisible]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Listen for custom event to toggle debug (e.g., from clicking name 10 times)
  useEffect(() => {
    const handleToggleDebug = () => setIsVisible((prev) => !prev);
    window.addEventListener("toggle-debug", handleToggleDebug);
    return () => window.removeEventListener("toggle-debug", handleToggleDebug);
  }, []);

  // Sync audio mute state
  useEffect(() => {
    setAudioMuted(spatialAudio.getMute());
  }, [isVisible]);

  // Listen for CRT state changes from page.tsx
  useEffect(() => {
    const handleCrtChange = (e: CustomEvent<boolean>) => setCrtEnabled(e.detail);
    window.addEventListener("crt-state-change", handleCrtChange as EventListener);
    return () => window.removeEventListener("crt-state-change", handleCrtChange as EventListener);
  }, []);

  // Toggle audio
  const handleAudioToggle = () => {
    const newMuted = !audioMuted;
    spatialAudio.setMute(newMuted);
    setAudioMuted(newMuted);
  };

  // Toggle CRT (dispatch event for page.tsx to handle)
  const handleCrtToggle = () => {
    const newEnabled = !crtEnabled;
    setCrtEnabled(newEnabled);
    window.dispatchEvent(new CustomEvent("toggle-crt", { detail: newEnabled }));
  };

  // Get CSS variable values
  const getThemeColors = () => {
    if (typeof window === "undefined") return { base: "", fg: "", bg: "" };
    const style = getComputedStyle(document.documentElement);
    return {
      base: style.getPropertyValue("--theme-base").trim(),
      fg: style.getPropertyValue("--theme-fg").trim(),
      bg: style.getPropertyValue("--background").trim(),
    };
  };

  const themeColors = isVisible ? getThemeColors() : { base: "", fg: "", bg: "" };
  const isTouch = typeof window !== "undefined" && "ontouchstart" in window;
  const pixelRatio = typeof window !== "undefined" ? window.devicePixelRatio : 1;
  const breakpoint = getBreakpoint(viewport.width);

  // Force GPU tier for testing (updates context globally)
  const handleForceGpu = (tier: number | null) => {
    setForcedGpuTier(tier);
    if (tier !== null) {
      setGpuTier(tier);
      if (tier <= 0) {
        setMode("off");
      } else {
        setMode("on");
      }
    }
  };

  return (
    <>
      {/* Grid overlay */}
      {showGrid && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99998,
            pointerEvents: "none",
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gap: 16,
            padding: "0 24px",
          }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              style={{
                background: "rgba(255, 0, 255, 0.08)",
                borderLeft: "1px solid rgba(255, 0, 255, 0.2)",
                borderRight: "1px solid rgba(255, 0, 255, 0.2)",
              }}
            />
          ))}
        </div>
      )}

      {/* Breakpoint indicator (always visible when debug is open) */}
      {isVisible && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            background: "rgba(0,0,0,0.9)",
            color: "#0f0",
            padding: "4px 8px",
            borderRadius: 4,
            fontSize: 11,
            fontFamily: "monospace",
            zIndex: 2147483647,
            pointerEvents: "none",
          }}
        >
          {breakpoint.toUpperCase()} • {viewport.width}×{viewport.height}
        </div>
      )}

      {/* Main debug panel */}
      {isVisible && (
        <div
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background: "rgba(0,0,0,0.95)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 8,
            padding: 16,
            zIndex: 2147483647, // Max z-index
            fontFamily: "monospace",
            fontSize: 11,
            color: "#fff",
            minWidth: 300,
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            isolation: "isolate",
            pointerEvents: "auto",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontWeight: "bold", color: "#0f0", fontSize: 12 }}>DEBUG</span>
            <button
              onClick={() => setIsVisible(false)}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "#fff",
                padding: "4px 8px",
                cursor: "pointer",
                fontSize: 10,
              }}
            >
              ESC
            </button>
          </div>

          {/* Performance */}
          <Section title="Performance">
            <Row label="FPS" value={fps} color={fps >= 55 ? "#0f0" : fps >= 30 ? "#ff0" : "#f00"} />
            <Row label="Scroll Y" value={`${scrollY}px (${scrollPercent}%)`} />
            <Row label="GSAP Active" value={gsapCount} color={gsapCount > 0 ? "#0ff" : "#888"} />
          </Section>

          {/* Viewport */}
          <Section title="Viewport">
            <Row label="Size" value={`${viewport.width} × ${viewport.height}`} />
            <Row label="Breakpoint" value={breakpoint.toUpperCase()} color="#0ff" />
            <Row label="Pixel Ratio" value={`${pixelRatio}x`} />
            <Row label="Touch" value={isTouch ? "Yes" : "No"} color={isTouch ? "#ff0" : "#888"} />
          </Section>

          {/* Motion */}
          <Section title="Motion">
            <Row label="GPU Tier" value={forcedGpuTier ?? gpuTier} color={gpuTier >= 2 ? "#0f0" : gpuTier === 1 ? "#ff0" : "#f00"} />
            <Row label="Motion Mode" value={motionMode} color={motionMode === "on" ? "#0f0" : "#f00"} />
            <Row label="Reduced Motion" value={reducedMotion ? "Yes" : "No"} color={reducedMotion ? "#f00" : "#0f0"} />
            <Row label="Can Animate" value={canAnimate ? "Yes" : "No"} color={canAnimate ? "#0f0" : "#f00"} />
            {/* visibilitychange fires on tab switch/minimize, NOT on window-blur */}
            <Row label="Tab Visible" value={backgrounded ? "No" : "Yes"} color={backgrounded ? "#f00" : "#0f0"} />
            <div style={{ marginTop: 8, display: "flex", gap: 4 }}>
              <MiniButton active={forcedGpuTier === null} onClick={() => handleForceGpu(null)}>Auto</MiniButton>
              <MiniButton active={forcedGpuTier === 3} onClick={() => handleForceGpu(3)} color="#0f0">T3</MiniButton>
              <MiniButton active={forcedGpuTier === 2} onClick={() => handleForceGpu(2)} color="#8f0">T2</MiniButton>
              <MiniButton active={forcedGpuTier === 1} onClick={() => handleForceGpu(1)} color="#ff0">T1</MiniButton>
              <MiniButton active={forcedGpuTier === 0} onClick={() => handleForceGpu(0)} color="#f00">T0</MiniButton>
            </div>
          </Section>

          {/* Audio & Effects */}
          <Section title="Audio & Effects">
            <Row label="Audio" value={audioMuted ? "Muted" : "On"} color={audioMuted ? "#f00" : "#0f0"} />
            <Row label="CRT Effects" value={crtEnabled ? "On" : "Off"} color={crtEnabled ? "#0f0" : "#888"} />
            <div style={{ marginTop: 8, display: "flex", gap: 4 }}>
              <MiniButton active={!audioMuted} onClick={handleAudioToggle} color="#0f0">
                {audioMuted ? "Unmute" : "Mute"}
              </MiniButton>
              <MiniButton active={crtEnabled} onClick={handleCrtToggle} color="#0ff">
                {crtEnabled ? "CRT Off" : "CRT On"}
              </MiniButton>
            </div>
          </Section>

          {/* Boot Sequence */}
          <Section title="Boot Sequence">
            <Row label="Phase" value={boot.phase.toUpperCase()} color="#ff0" />
            <Row label="Progress" value={`${Math.round(boot.phaseProgress * 100)}%`} />
            <Row label="Playing" value={boot.isPlaying ? "Yes" : "No"} color={boot.isPlaying ? "#0f0" : "#f00"} />
            <Row label="Complete" value={boot.isComplete ? "Yes" : "No"} color={boot.isComplete ? "#0f0" : "#888"} />
            <Row label="Elapsed" value={`${(boot.totalElapsed / 1000).toFixed(1)}s`} />
            <div style={{ marginTop: 8, display: "flex", gap: 4 }}>
              <MiniButton onClick={boot.skip} color="#ff0">Skip</MiniButton>
              <MiniButton onClick={boot.togglePause} color={boot.isPlaying ? "#f00" : "#0f0"}>
                {boot.isPlaying ? "Pause" : "Play"}
              </MiniButton>
            </div>
          </Section>

          {/* State */}
          <Section title="State">
            <Row label="Route" value={pathname} />
            <Row label="Theme" value={themeMode} />
            <Row label="Loader" value={isLoaderActive ? "Active" : "Done"} color={isLoaderActive ? "#ff0" : "#0f0"} />
          </Section>

          {/* Theme Colors */}
          <Section title="Theme Colors">
            <ColorRow label="Base" value={themeColors.base} />
            <ColorRow label="Foreground" value={themeColors.fg} />
            <ColorRow label="Background" value={themeColors.bg} />
          </Section>

          {/* Tools */}
          <Section title="Tools">
            <div style={{ display: "flex", gap: 8 }}>
              <MiniButton active={showGrid} onClick={() => setShowGrid(!showGrid)}>
                {showGrid ? "Hide Grid" : "Show Grid"}
              </MiniButton>
            </div>
          </Section>

          {/* Footer */}
          <div style={{ marginTop: 12, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.1)", color: "#444", fontSize: 9 }}>
            ↑↑↓↓←→←→ Space to toggle
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: "#666", fontSize: 9, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.1em" }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
    </div>
  );
}

function Row({ label, value, color = "#fff" }: { label: string; value: string | number; color?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color: "#666", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ flex: 1, borderBottom: "1px dotted rgba(255,255,255,0.15)" }} />
      <span style={{ color, whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

function ColorRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color: "#666", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ flex: 1, borderBottom: "1px dotted rgba(255,255,255,0.15)", marginBottom: 0 }} />
      <span style={{ color: "#fff", whiteSpace: "nowrap" }}>{value || "—"}</span>
      {value && (
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: value,
            border: "1px solid rgba(255,255,255,0.3)",
            flexShrink: 0,
          }}
        />
      )}
    </div>
  );
}

function MiniButton({ children, onClick, active, color = "#0f0" }: { children: React.ReactNode; onClick: () => void; active?: boolean; color?: string }) {
  // Convert hex color to rgba for backgrounds
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  return (
    <button
      onClick={onClick}
      style={{
        position: "relative",
        background: active ? hexToRgba(color, 0.25) : "rgba(255,255,255,0.1)",
        border: `1px solid ${active ? hexToRgba(color, 0.6) : "rgba(255,255,255,0.2)"}`,
        borderRadius: 4,
        color: active ? color : "#888",
        padding: "4px 8px 6px",
        cursor: "pointer",
        fontSize: 10,
      }}
    >
      {children}
      {active && (
        <span
          style={{
            position: "absolute",
            bottom: 2,
            left: "50%",
            transform: "translateX(-50%)",
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 4px ${color}`,
          }}
        />
      )}
    </button>
  );
}
